import type { PullResult } from '@gachakit/core/types/pack';
import { animateReveal } from '../animations/reveal';
import { triggerRarityFeedback } from '../animations/rarity-effects';

type RevealPhase = 'sealed' | 'opening' | 'stack' | 'summary';

const styles = `
  :host {
    position: fixed;
    inset: 0;
    display: none;
    place-items: stretch;
    padding:
      calc(12px + env(safe-area-inset-top))
      12px
      calc(12px + env(safe-area-inset-bottom));
    background:
      linear-gradient(180deg, rgba(8, 9, 8, 0.98), rgba(8, 9, 8, 0.94)),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 17px);
    backdrop-filter: blur(22px);
    z-index: 30;
  }

  :host([open]) {
    display: grid;
  }

  .wrap {
    width: min(100%, 460px);
    min-height: 100%;
    margin: 0 auto;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    gap: 10px;
  }

  .hud {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
  }

  .pill {
    min-height: 34px;
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    border-radius: 999px;
    color: rgba(247,239,218,0.76);
    background: rgba(247,239,218,0.08);
    border: 1px solid rgba(247,239,218,0.12);
    font: 900 0.72rem/1 var(--font-display, "Sora", sans-serif);
  }

  .stage {
    --rarity-color: #9eaa9b;
    position: relative;
    min-height: 0;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid color-mix(in oklab, var(--rarity-color) 36%, rgba(255,255,255,0.12));
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 16%, transparent), transparent 42%),
      linear-gradient(180deg, rgba(247,239,218,0.1), rgba(247,239,218,0.025)),
      #0d100d;
    box-shadow: 0 24px 58px rgba(0,0,0,0.54);
  }

  .stage::before,
  .stage::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .stage::before {
    background:
      linear-gradient(118deg, transparent 0 34%, rgba(255,255,255,0.12) 48%, transparent 62%),
      repeating-linear-gradient(135deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 12px);
    mix-blend-mode: screen;
    opacity: 0.62;
  }

  .stage::after {
    border-radius: inherit;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.05);
  }

  .stage[data-rarity='common'] { --rarity-color: var(--rarity-common, #9eaa9b); }
  .stage[data-rarity='uncommon'] { --rarity-color: var(--rarity-uncommon, #58d68d); }
  .stage[data-rarity='rare'] { --rarity-color: var(--rarity-rare, #68c7ff); }
  .stage[data-rarity='epic'] { --rarity-color: var(--rarity-epic, #e16f9d); }
  .stage[data-rarity='legendary'] { --rarity-color: var(--rarity-legendary, #f5c76b); }

  .sealed,
  .opening,
  .stack,
  .summary {
    position: relative;
    z-index: 1;
    min-height: 100%;
  }

  .sealed,
  .opening {
    display: grid;
    place-items: center;
    padding: 18px 18px 24px;
  }

  .pack-shell {
    width: 100%;
    display: grid;
    justify-items: center;
    gap: 14px;
  }

  .pack {
    position: relative;
    width: min(100%, 322px);
    aspect-ratio: 0.58;
    border: 0;
    border-radius: 8px;
    padding: 0;
    overflow: hidden;
    background:
      linear-gradient(145deg, rgba(255,255,255,0.22), transparent 46%),
      #11140f;
    box-shadow:
      0 28px 50px rgba(0,0,0,0.48),
      0 0 0 1px rgba(255,255,255,0.12) inset;
    transform: rotate(-1.5deg);
  }

  .pack::before,
  .pack::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    z-index: 2;
    height: 26px;
    background:
      repeating-linear-gradient(90deg, rgba(255,255,255,0.22) 0 12px, rgba(8,9,8,0.24) 12px 24px);
  }

  .pack::before { top: 0; }
  .pack::after { bottom: 0; }

  .pack-art {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  .seal-line {
    position: absolute;
    z-index: 3;
    left: 16px;
    right: 16px;
    top: 48%;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.82), transparent);
    box-shadow: 0 0 18px rgba(255,255,255,0.28);
  }

  .hint {
    max-width: 30ch;
    text-align: center;
    color: rgba(247,239,218,0.72);
    font-size: 0.9rem;
    line-height: 1.35;
  }

  .opening .pack {
    animation: rip-pack 700ms cubic-bezier(0.18, 0.9, 0.22, 1) forwards;
  }

  .flash {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(115deg, transparent 0 30%, color-mix(in oklab, var(--rarity-color) 48%, #ffffff) 48%, transparent 68%);
    opacity: 0;
    animation: reveal-flash 700ms ease-out forwards;
  }

  .stack {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    gap: 10px;
    padding: 14px;
  }

  .stack-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .stack-title {
    min-height: 36px;
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    border-radius: 999px;
    color: #07100c;
    background: linear-gradient(135deg, color-mix(in oklab, var(--rarity-color) 82%, #ffffff), var(--rarity-color));
    font: 900 0.78rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  .counter {
    color: rgba(247,239,218,0.74);
    font: 900 0.82rem/1 var(--font-display, "Sora", sans-serif);
  }

  .stack-board {
    position: relative;
    min-height: 0;
    display: grid;
    place-items: center;
    perspective: 1200px;
  }

  .rear-card,
  .front-slot,
  .card-back {
    position: absolute;
    width: min(72vw, 248px);
    max-width: 248px;
    aspect-ratio: 0.72;
    border-radius: 16px;
  }

  .rear-card {
    overflow: hidden;
    border: 1px solid color-mix(in oklab, var(--rarity-color) 42%, rgba(255,255,255,0.14));
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 18%, transparent), rgba(255,255,255,0.05)),
      #11140f;
    box-shadow: 0 16px 34px rgba(0,0,0,0.32);
    background-size: cover;
    background-position: center;
  }

  .rear-card::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.54)),
      linear-gradient(125deg, transparent 0 38%, rgba(255,255,255,0.12) 50%, transparent 62%);
  }

  .rear-card[data-order='1'] { transform: translate3d(48px, 8px, -40px) rotate(8deg); }
  .rear-card[data-order='2'] { transform: translate3d(30px, 16px, -80px) rotate(5deg); }
  .rear-card[data-order='3'] { transform: translate3d(12px, 24px, -120px) rotate(1deg); }
  .rear-card[data-order='4'] { transform: translate3d(-6px, 30px, -160px) rotate(-3deg); }

  .front-slot {
    display: grid;
    place-items: center;
    transform-style: preserve-3d;
  }

  .front-slot.collecting {
    pointer-events: none;
  }

  .card-back {
    display: grid;
    place-items: center;
    border: 1px solid rgba(247,239,218,0.14);
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 22%, transparent), rgba(255,255,255,0.05)),
      repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 12px),
      #11140f;
    color: rgba(247,239,218,0.72);
    font: 900 0.9rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  gacha-card {
    width: min(72vw, 248px);
    max-width: 248px;
  }

  .stack-actions {
    display: grid;
    gap: 7px;
    justify-items: center;
  }

  .binder-target {
    min-height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid rgba(247,239,218,0.12);
    background: rgba(247,239,218,0.07);
    color: rgba(247,239,218,0.72);
    font-size: 0.75rem;
    text-align: center;
  }

  .binder-dot {
    width: 13px;
    height: 17px;
    border-radius: 3px;
    background: linear-gradient(135deg, #a7f5ce, #f5c76b);
    transform: rotate(-8deg);
    box-shadow: 0 0 18px rgba(167,245,206,0.34);
  }

  .tap,
  .close {
    width: min(100%, 360px);
    min-height: 46px;
    border: 0;
    border-radius: 8px;
    color: #07100c;
    background: linear-gradient(135deg, #a7f5ce, #f5c76b);
    font: 900 0.88rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  .tap:active,
  .close:active {
    transform: translateY(1px) scale(0.99);
  }

  .summary {
    display: grid;
    align-content: center;
    gap: 14px;
    padding: 18px;
  }

  .summary-card {
    padding: 16px;
    border-radius: 8px;
    border: 1px solid rgba(247,239,218,0.12);
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 18%, transparent), rgba(247,239,218,0.05)),
      rgba(8, 9, 8, 0.44);
  }

  .summary-card h3 {
    margin: 0;
    color: #fff8e8;
    font: 900 1.26rem/1 var(--font-display, "Sora", sans-serif);
  }

  .summary-card p {
    margin: 8px 0 0;
    color: rgba(247,239,218,0.72);
    line-height: 1.35;
    font-size: 0.9rem;
  }

  .summary-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .summary-metrics span {
    min-width: 0;
    padding: 13px 8px;
    border-radius: 8px;
    background: rgba(247,239,218,0.06);
    border: 1px solid rgba(247,239,218,0.1);
    text-align: center;
  }

  .summary-metrics strong,
  .summary-metrics small {
    display: block;
  }

  .summary-metrics strong {
    color: #fff8e8;
    font: 900 1.1rem/1 var(--font-display, "Sora", sans-serif);
  }

  .summary-metrics small {
    margin-top: 7px;
    color: rgba(247,239,218,0.65);
    font-size: 0.7rem;
  }

  @keyframes rip-pack {
    0% {
      opacity: 1;
      transform: rotate(-1.5deg) scale(1);
      clip-path: inset(0 0 0 0);
    }

    46% {
      opacity: 1;
      transform: rotate(1deg) scale(1.04);
      clip-path: inset(0 0 0 0);
    }

    100% {
      opacity: 0;
      transform: rotate(7deg) translateY(-28px) scale(0.88);
      clip-path: inset(0 0 54% 0);
    }
  }

  @keyframes reveal-flash {
    0% { opacity: 0; transform: translateX(-24%); }
    26% { opacity: 0.95; }
    100% { opacity: 0; transform: translateX(24%); }
  }

  @media (max-width: 374px) {
    :host {
      padding-inline: 8px;
    }

    .rear-card,
    .front-slot,
    .card-back,
    gacha-card {
      width: min(68vw, 226px);
    }

    .binder-target {
      font-size: 0.7rem;
    }
  }

  @media (max-height: 700px) {
    :host {
      padding-block:
        calc(8px + env(safe-area-inset-top))
        calc(8px + env(safe-area-inset-bottom));
    }

    .wrap,
    .stack {
      gap: 8px;
    }

    .stack {
      padding: 12px;
    }

    .rear-card,
    .front-slot,
    .card-back,
    gacha-card {
      width: min(68vw, 226px);
    }

    .binder-target {
      min-height: 34px;
      font-size: 0.7rem;
    }

    .tap,
    .close {
      min-height: 42px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .opening .pack,
    .flash {
      animation: none;
    }
  }
`;

export class PullResultElement extends HTMLElement {
  private readonly root: ShadowRoot;
  private currentPack: PullResult[] = [];
  private currentIndex = 0;
  private phase: RevealPhase = 'sealed';
  private loadedImages = new Set<number>();
  private preloadToken = 0;
  private openingTimeout: number | null = null;
  private collecting = false;
  private feedbackIndex = -1;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  disconnectedCallback(): void {
    this.clearOpeningTimeout();
  }

  set pack(value: PullResult[] | null) {
    this.clearOpeningTimeout();
    this.currentPack = value ? [...value] : [];
    this.currentIndex = 0;
    this.phase = 'sealed';
    this.collecting = false;
    this.feedbackIndex = -1;
    this.loadedImages = new Set<number>();
    this.preloadToken += 1;
    this.toggleAttribute('open', this.currentPack.length > 0);

    if (this.currentPack.length > 0) {
      void this.preloadPack(this.preloadToken);
    }

    this.render();
  }

  get pack(): PullResult[] | null {
    return this.currentPack.length > 0 ? this.currentPack : null;
  }

  private render(): void {
    if (this.currentPack.length === 0) {
      this.root.innerHTML = '';
      return;
    }

    const current = this.currentPack[this.currentIndex] ?? this.currentPack[this.currentPack.length - 1];
    const displayIndex = Math.min(this.currentIndex + 1, this.currentPack.length);
    this.root.innerHTML = `
      <style>${styles}</style>
      <div class="wrap">
        <div class="hud">
          <span class="pill">${phaseLabel(this.phase)}</span>
          <span class="pill">${displayIndex}/${this.currentPack.length}</span>
        </div>
        <div class="stage" data-rarity="${current.card.rarity}">
          ${this.renderPhase()}
        </div>
      </div>
    `;

    if (this.phase === 'sealed') {
      this.root.querySelector('.pack')?.addEventListener('click', () => this.openPack(), { once: true });
    }

    if (this.phase === 'stack') {
      this.hydrateFrontCard();
      this.root.querySelector('.front-slot')?.addEventListener('click', () => this.collectCurrentCard(), { once: true });
      this.root.querySelector('.tap')?.addEventListener('click', () => this.collectCurrentCard(), { once: true });
    }

    if (this.phase === 'summary') {
      this.root.querySelector('.close')?.addEventListener('click', () => this.close(), { once: true });
    }
  }

  private renderPhase(): string {
    switch (this.phase) {
      case 'sealed':
        return this.renderSealed();
      case 'opening':
        return this.renderOpening();
      case 'stack':
        return this.renderStack();
      case 'summary':
        return this.renderSummary();
    }
  }

  private renderSealed(): string {
    const firstReady = this.loadedImages.has(0);

    return `
      <div class="sealed">
        <div class="pack-shell">
          <button class="pack" type="button" ${firstReady ? '' : 'disabled'}>
            <img class="pack-art" src="/pack/sotw.webp" alt="Species Gacha sealed booster" loading="eager" />
            <span class="seal-line" aria-hidden="true"></span>
          </button>
          <div class="hint">${firstReady ? 'Tap the foil seam to open the field pack.' : 'Preloading the lead specimen...'}</div>
        </div>
      </div>
    `;
  }

  private renderOpening(): string {
    return `
      <div class="opening">
        <div class="flash"></div>
        <div class="pack-shell">
          <div class="pack">
            <img class="pack-art" src="/pack/sotw.webp" alt="Species Gacha sealed booster" loading="eager" />
            <span class="seal-line" aria-hidden="true"></span>
          </div>
          <div class="hint">The stack is sliding out of the wrapper.</div>
        </div>
      </div>
    `;
  }

  private renderStack(): string {
    const current = this.currentPack[this.currentIndex];
    const nextCards = this.currentPack.slice(this.currentIndex + 1, this.currentIndex + 5);
    const rearCards = nextCards
      .map((result, index) => {
        const packIndex = this.currentIndex + index + 1;
        const loaded = this.loadedImages.has(packIndex);
        const background = loaded ? `style="background-image:url('${escapeAttribute(result.card.imageUrl ?? '')}')"` : '';
        return `
          <div class="rear-card" data-order="${index + 1}" data-loaded="${loaded}" data-rarity="${result.card.rarity}" ${background}></div>
        `;
      })
      .reverse()
      .join('');

    return `
      <div class="stack">
        <div class="stack-top">
          <div class="stack-title">${current.card.rarity}</div>
          <span class="counter">${this.currentIndex + 1}/${this.currentPack.length}</span>
        </div>
        <div class="stack-board">
          ${rearCards}
          <div class="front-slot ${this.collecting ? 'collecting' : ''}">
            ${
              this.loadedImages.has(this.currentIndex)
                ? '<gacha-card compact></gacha-card>'
                : '<div class="card-back">Loading</div>'
            }
          </div>
        </div>
        <div class="stack-actions">
          <div class="binder-target">
            <span class="binder-dot" aria-hidden="true"></span>
            <span>Tap the front card to file it in the binder</span>
          </div>
          <button class="tap" type="button">Collect Card</button>
        </div>
      </div>
    `;
  }

  private renderSummary(): string {
    const newCards = this.currentPack.filter((result) => result.isNew).length;
    const rareCards = this.currentPack.filter((result) =>
      ['rare', 'epic', 'legendary'].includes(result.card.rarity)
    ).length;

    return `
      <div class="summary">
        <div class="summary-card">
          <h3>Filed to binder</h3>
          <p>All five cards are saved locally. The rarest pull defines this pack's holo treatment.</p>
        </div>
        <div class="summary-metrics">
          <span><strong>${this.currentPack.length}</strong><small>Cards</small></span>
          <span><strong>${newCards}</strong><small>New</small></span>
          <span><strong>${rareCards}</strong><small>Rare+</small></span>
        </div>
        <button class="close" type="button">Return to Vault</button>
      </div>
    `;
  }

  private async preloadPack(token: number): Promise<void> {
    const firstCard = this.currentPack[0]?.card;
    if (firstCard) {
      await preloadImage(firstCard.imageUrl);
      if (token !== this.preloadToken) {
        return;
      }
      this.loadedImages.add(0);
      this.render();
    }

    this.currentPack.slice(1).forEach((result, offset) => {
      const packIndex = offset + 1;
      void preloadImage(result.card.imageUrl).then(() => {
        if (token !== this.preloadToken) {
          return;
        }
        this.loadedImages.add(packIndex);
        if (this.phase === 'stack') {
          this.render();
        }
      });
    });
  }

  private hydrateFrontCard(): void {
    const current = this.currentPack[this.currentIndex];
    const cardElement = this.root.querySelector('gacha-card') as (HTMLElement & {
      card: PullResult['card'];
    }) | null;

    if (!cardElement || !current) {
      return;
    }

    cardElement.setAttribute('compact', '');
    cardElement.setAttribute('data-status', current.isNew ? 'new file' : 'duplicate');
    cardElement.card = current.card;
    animateReveal(cardElement);

    if (this.feedbackIndex !== this.currentIndex) {
      this.feedbackIndex = this.currentIndex;
      triggerRarityFeedback(current.card.rarity);
    }
  }

  private openPack(): void {
    if (!this.loadedImages.has(0)) {
      return;
    }

    this.phase = 'opening';
    this.render();

    this.clearOpeningTimeout();
    this.openingTimeout = window.setTimeout(() => {
      this.phase = 'stack';
      this.render();
    }, 700);
  }

  private async collectCurrentCard(): Promise<void> {
    if (this.collecting || this.phase !== 'stack') {
      return;
    }

    this.collecting = true;
    const cardSlot = this.root.querySelector('.front-slot') as HTMLElement | null;
    const binder = this.root.querySelector('.binder-target') as HTMLElement | null;

    if (cardSlot && binder && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const cardRect = cardSlot.getBoundingClientRect();
      const binderRect = binder.getBoundingClientRect();
      const translateX = binderRect.left + binderRect.width / 2 - (cardRect.left + cardRect.width / 2);
      const translateY = binderRect.top + binderRect.height / 2 - (cardRect.top + cardRect.height / 2);

      const animation = cardSlot.animate(
        [
          { transform: 'translate3d(0,0,0) scale(1) rotate(0deg)', opacity: 1 },
          {
            transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(0.14) rotate(18deg)`,
            opacity: 0.12
          }
        ],
        {
          duration: 500,
          easing: 'cubic-bezier(0.18, 0.9, 0.22, 1)',
          fill: 'forwards'
        }
      );

      await animation.finished.catch(() => undefined);
    }

    this.currentIndex += 1;
    this.collecting = false;

    if (this.currentIndex >= this.currentPack.length) {
      this.phase = 'summary';
      this.render();
      return;
    }

    this.phase = 'stack';
    this.render();
  }

  private clearOpeningTimeout(): void {
    if (this.openingTimeout !== null) {
      window.clearTimeout(this.openingTimeout);
      this.openingTimeout = null;
    }
  }

  private close(): void {
    this.pack = null;
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }
}

if (!customElements.get('pull-result')) {
  customElements.define('pull-result', PullResultElement);
}

function phaseLabel(phase: RevealPhase): string {
  switch (phase) {
    case 'sealed':
      return 'Sealed pack';
    case 'opening':
      return 'Wrapper split';
    case 'stack':
      return 'Filing cards';
    case 'summary':
      return 'Pack summary';
  }
}

function preloadImage(url: string | undefined): Promise<void> {
  if (!url) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'high';
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = url;
  });
}

function escapeAttribute(value: string): string {
  return value.replaceAll("'", '%27').replaceAll('"', '&quot;');
}
